import { RisksId } from "./risk";
import { ReactComponent as NonResidentialBuilding } from "../statics/icons/non_residential_building.svg";
import { ReactComponent as FixedEstateElements } from "../statics/icons/fixed_estate_elements.svg";
import { ReactComponent as GreenEnergySources } from "../statics/icons/green_energy_sources.svg";
import { ReactComponent as CatAndDog } from "../statics/icons/cat_and_dog.svg";
import { ReactComponent as LegalProtection } from "../statics/icons/legal_protection.svg";
import { ReactComponent as SafeSenior } from "../statics/icons/safe_senior.svg";
import { ReactComponent as Gravestone } from "../statics/icons/gravestone.svg";
import { ReactComponent as SecondOpinion } from "../statics/icons/doctor.svg";
import React from "react";
import { LegalProtectionVariant } from "./legalProtection";
import { CatAndDogModel, DefaultCatAndDog } from "./catAndDog";
import { DefaultOcStandard, OcAdditionalProperty, OcStandardModel } from "./oc";
import { SimpleAddressClass } from "./address";
import { DefaultNnwType, NnwHeartAttackAndStrokeModel, NnwSeniorModel, NnwTypeModel } from "./nnw";
import { CountId } from "./dropDown";
import { GravestoneInsuranceSumId, GravestoneModel } from "./gravestone";
import { SafeSeniorModel } from "./safeSenior";
import { RisksNetworkResponseModel, RisksNetworkTypes } from "./risksNetwork";
import Formatter from "./formatter";
import { CalculateStore } from "../stores/calculateStore";
import { makeObservable, observable } from "mobx";
import { ErrorMessages } from "./errorMessage";

export interface SimpleAdditionalInsuranceModel {
  checked: boolean;
  rate: number | null;
  risksNetworkValue?: RisksNetworkTypes | undefined;
  getRisksNetworkValue?: (response: RisksNetworkResponseModel) => RisksNetworkTypes;
  getValuesFromRetrievedCalculation?: (insurance: any, calculateStore: CalculateStore) => void;
}

export interface OnlyInsuranceSumModel extends SimpleAdditionalInsuranceModel {
  insuranceSum: number;
}

export interface LegalProtectionModel extends SimpleAdditionalInsuranceModel {
  variant: LegalProtectionVariant | null;
  validate: () => void;
  errorMessage: string;
}

export interface NonResidentialBuildingModel extends SimpleAdditionalInsuranceModel {
  flammableStructure: boolean | undefined;
  insuranceSum: number;
  validate: () => void;
  errorMessage: string;
}

export interface AdditionalInsurancesModel {
  [RisksId.nonResidentialBuilding]: NonResidentialBuildingModel;
  [RisksId.fixedEstateElements]: OnlyInsuranceSumModel;
  [RisksId.greenEnergySources]: OnlyInsuranceSumModel;
  [RisksId.catAndDog]: CatAndDogModel;
  [RisksId.ocWithoutProperty]: SimpleAdditionalInsuranceModel;
  [RisksId.ocStandard]: OcStandardModel;
  [RisksId.ocAdditionalProperty]: OcAdditionalProperty;
  [RisksId.ocChildExtension]: SimpleAdditionalInsuranceModel;
  [RisksId.ocRenterExtension]: SimpleAdditionalInsuranceModel;
  [RisksId.nnwWithoutProperty]: SimpleAdditionalInsuranceModel;
  [RisksId.nnwType]: NnwTypeModel;
  [RisksId.nnwSenior]: NnwSeniorModel;
  [RisksId.nnwHeartAttackAndStroke]: NnwHeartAttackAndStrokeModel;
  [RisksId.legalProtection]: LegalProtectionModel;
  [RisksId.gravestone]: GravestoneModel;
  [RisksId.safeSenior]: SafeSeniorModel;
  [RisksId.secondOpinion]: SimpleAdditionalInsuranceModel;
  [RisksId.secondOpinionExtension]: SimpleAdditionalInsuranceModel;
}

export class DefaultAdditionalInsurances implements AdditionalInsurancesModel {
  @observable nonResidentialBuilding: NonResidentialBuildingModel;
  @observable fixedEstateElements: OnlyInsuranceSumModel;
  @observable greenEnergySources: OnlyInsuranceSumModel;
  @observable catAndDog: CatAndDogModel;
  @observable ocWithoutProperty: SimpleAdditionalInsuranceModel;
  @observable ocStandard: OcStandardModel;
  @observable ocAdditionalProperty: OcAdditionalProperty;
  @observable ocChildExtension: SimpleAdditionalInsuranceModel;
  @observable ocRenterExtension: SimpleAdditionalInsuranceModel;
  @observable legalProtection: LegalProtectionModel;
  @observable nnwWithoutProperty: SimpleAdditionalInsuranceModel;
  @observable nnwType: NnwTypeModel;
  @observable nnwSenior: NnwSeniorModel;
  @observable nnwHeartAttackAndStroke: NnwHeartAttackAndStrokeModel;
  @observable gravestone: GravestoneModel;
  @observable safeSenior: SafeSeniorModel;
  @observable secondOpinion: SimpleAdditionalInsuranceModel;
  @observable secondOpinionExtension: SimpleAdditionalInsuranceModel;

  constructor() {
    makeObservable(this);

    this.nonResidentialBuilding = {
      checked: false,
      flammableStructure: undefined,
      insuranceSum: 0,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.nonResidentialBuildings,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.nonResidentialBuilding.insuranceSum = Formatter.convertCentsToNormalNumber(insurance.nonResidentialBuildings?.insuranceSumInCents ?? 0);
        this.nonResidentialBuilding.flammableStructure = insurance.nonResidentialBuildings?.flammableStructure ?? undefined;
        this.nonResidentialBuilding.checked = !!insurance.nonResidentialBuildings;
      },
      errorMessage: "",
      validate: () => {
        const self = this.nonResidentialBuilding;
        if (self.checked && self.flammableStructure === undefined) {
          self.errorMessage = ErrorMessages.required;
        } else {
          self.errorMessage = "";
        }
      }
    };
    this.fixedEstateElements = {
      checked: false,
      insuranceSum: 0,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.propertyFixedElements,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.fixedEstateElements.insuranceSum = Formatter.convertCentsToNormalNumber(insurance.propertyFixedElements?.insuranceSumInCents ?? 0);
        this.fixedEstateElements.checked = !!insurance.propertyFixedElements;
      }
    };
    this.greenEnergySources = {
      checked: false,
      insuranceSum: 0,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.greenEnergySources,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.greenEnergySources.insuranceSum = Formatter.convertCentsToNormalNumber(insurance.greenEnergySources?.insuranceSumInCents ?? 0);
        this.greenEnergySources.checked = !!insurance.greenEnergySources;
      }
    };
    this.catAndDog = new DefaultCatAndDog();
    this.ocWithoutProperty = {
      checked: false,
      rate: null,
      getValuesFromRetrievedCalculation: (insurance: any, calculateStore: CalculateStore) => {
        this.ocWithoutProperty.checked = calculateStore.oc;
      }
    };
    this.ocStandard = DefaultOcStandard;
    this.ocAdditionalProperty = {
      checked: false,
      address: SimpleAddressClass.default(),
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.ocInPrivateLife.additionalPropertyExtension,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.ocAdditionalProperty.checked = !!insurance.ocInPrivateLife?.additionalProperty;
        this.ocAdditionalProperty.address = new SimpleAddressClass({ ...insurance.ocInPrivateLife?.additionalProperty?.address });
      },
      validate: () => {
        const self = this.ocAdditionalProperty;
        if (self.checked) {
          self.address.buildingAndApartmentNumber.validate();
          self.address.city.validate();
          self.address.street.validate();
          self.address.zipCode.validate();
        } else {
          self.address.buildingAndApartmentNumber.clearValidation();
          self.address.city.clearValidation();
          self.address.street.clearValidation();
          self.address.zipCode.clearValidation();
        }
      }
    };
    this.ocChildExtension = {
      checked: false,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.ocInPrivateLife.childExtension,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.ocChildExtension.checked = !!insurance.ocInPrivateLife?.childExtension;
      }
    };
    this.ocRenterExtension = {
      checked: false,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.ocInPrivateLife.renterExtension,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.ocRenterExtension.checked = !!insurance.ocInPrivateLife?.renterExtension;
      }
    };
    this.legalProtection = {
      checked: false,
      variant: null,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.legalProtection,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.legalProtection.variant = insurance.legalProtection?.variant ?? null;
        this.legalProtection.checked = !!insurance.legalProtection;
      },
      errorMessage: "",
      validate: () => {
        const self = this.legalProtection;
        if (self.checked && !self.variant) {
          self.errorMessage = ErrorMessages.required;
        } else {
          self.errorMessage = "";
        }
      }
    };
    this.nnwWithoutProperty = {
      checked: false,
      rate: null,
      getValuesFromRetrievedCalculation: (insurance: any, calculateStore: CalculateStore) => {
        this.nnwWithoutProperty.checked = calculateStore.nnw;
      }
    };
    this.nnwType = DefaultNnwType;
    this.nnwSenior = {
      checked: false,
      nnwNumberOfSeniors: CountId.zero,
      rate: null,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.nnwSenior.nnwNumberOfSeniors = insurance.nnw?.numberOfClientsAbove60years ?? CountId.zero;
        this.nnwSenior.checked = !!insurance.nnw?.numberOfClientsAbove60years;
      }
    };
    this.nnwHeartAttackAndStroke = {
      checked: false,
      numberOfClients: CountId.zero,
      rate: null,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.nnwHeartAttackAndStroke.numberOfClients = insurance.nnw?.extendedForHeartAttackAndStroke ?? CountId.zero;
        this.nnwHeartAttackAndStroke.checked = !!insurance.nnw?.extendedForHeartAttackAndStroke;
      }
    };
    this.gravestone = {
      checked: false,
      insuranceSum: GravestoneInsuranceSumId.fiveThousands,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.gravestone,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.gravestone.insuranceSum = insurance.gravestone?.insuranceSum ?? GravestoneInsuranceSumId.fiveThousands;
        this.gravestone.checked = !!insurance.gravestone;
      }
    };
    this.safeSenior = {
      checked: false,
      safeSeniorCount: CountId.one,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.safeSenior,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.safeSenior.safeSeniorCount = insurance.safeSenior?.numberOfInsureds ?? CountId.one;
        this.safeSenior.checked = !!insurance.safeSenior;
      }
    };
    this.secondOpinion = {
      checked: false,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.secondOpinion.standard,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.secondOpinion.checked = !!insurance.secondOpinion?.standard;
      }
    };
    this.secondOpinionExtension = {
      checked: true,
      rate: null,
      getRisksNetworkValue: (response: RisksNetworkResponseModel) => response.secondOpinion.familyExtension,
      getValuesFromRetrievedCalculation: (insurance: any) => {
        this.secondOpinionExtension.checked = !!insurance.secondOpinion?.familyExtension;
      }
    };
  }
}

export interface AdditionalInsuranceNameModel {
  displayName: React.ReactNode | string;
  displayNotification?: string;
  icon?: React.ReactNode;
  contextHelp?: React.ReactNode;
}

export interface AdditionalInsurancesNameModel {
  [key: string]: AdditionalInsuranceNameModel;
}

export const AdditionalInsurancesName: AdditionalInsurancesNameModel = {
  [RisksId.nonResidentialBuilding]: {
    displayName: "Budynki niemieszkalne",
    icon: <NonResidentialBuilding className="icon-svg-color" />,
    contextHelp: `<div>
                <div>Wpisz łączną sumę ubezpieczenia budynków niemieszkalnych: budynku gospodarczego i garażu.</div>
                <div>&nbsp;</div>
                <div>
                    <b>Uwaga!</b> Budynkiem gospodarczym nie jest garaż, budynek gospodarczy wchodzący w skład
                    gospodarstwa rolnego lub budynek niemieszkalny w którym całość lub więcej niż 50% powierzchni
                    przeznaczona jest na działalność gospodarczą.
                </div>
            </div>`
  },
  [RisksId.fixedEstateElements]: {
    displayName: "Stałe elementy posesji",
    icon: <FixedEstateElements className="icon-svg-color" />,
    contextHelp: `<div>
                <div>
                    Wpisz łączną sumę ubezpieczenia budowli i obiektów małej architektury wraz z ich
                    zewnętrznymi stałymi elementami, trwale związane z gruntem takich jak np.: wiata, altana, basen,
                    śmietnik, lampa ogrodowa, grill, urządzenia sportowe, nawierzchnie placów, pompa ciepła,
                    zbiornik na gaz lub paliwo płynne, przydomowa oczyszczalnia ścieków, szambo, instalacja systemu
                    nawadniania ogrodu, taras nieuwzględniony w projekcie domu, ogrodzenie posesji wraz z bramą i
                    ich stałe elmenty zewnętrzne.
                </div>
                <div>&nbsp;</div>
                <div>
                    <b>Uwaga!</b> Budowlą nie jest budynek, budynek tymczasowy, lekka konstrukcja np. trampolina,
                    namiot, basen naziemny, parasol ogrodowy.
                </div>
            </div>`
  },
  [RisksId.greenEnergySources]: {
    displayName: "Zielone źródła energii",
    icon: <GreenEnergySources className="icon-svg-color" />,
    contextHelp: `<div>
                Wpisz sumę ubezpieczenia systemów fotowoltaicznych i solarnych wraz z ich instalacją, znajdujące się
                w miejscu ubezpieczenia, <b>trwale związane z gruntem lub jako element budynku, mieszkania </b>
                zamontowane w sposób trwały (w sposób uniemożliwiający ich odłączenie bez użycia narzędzi).
            </div>`
  },
  [RisksId.catAndDog]: {
    displayName: "Ubezpieczenie Pies i Kot",
    icon: <CatAndDog className="icon-svg-color" />,
    contextHelp: `<div>
                Ubezpiecz swojego psa i/lub kota, a w wyniku zdarzenia drogowego z udziałem twojego pupila lub
                pogryzienia przez inne zwierzę, otrucia w związku z dokonaną kradzieżą lub w wyniku pożaru czy dymu twój
                pupil dozna obrażeń lub nie przeżyje - zwrócimy koszt leczenia weterynaryjnego w tym fizjoterapii i kosztów protez,
                transportu do placówki, uśpienia z konieczności, kremacji i pochówku, a także wypłacimy świadczenie po śmierci zwierzęcia.
                <div>
                    <b>
                        Możesz ubezpieczyć 4 zwierzęta, które z Tobą przebywają w miejscu ubezpieczenia.
                        Wskazana Suma Ubezpieczenia jest na jedno zwierzę, czyli każdy pies lub kot jest ubezpieczony na taką kwotę jaką wskażesz.
                    </b>
                </div>
            </div>`
  },
  [RisksId.ocWithoutProperty]: {
    displayName: "Ubezpieczenie OC w życiu prywatnym bez ubezpieczenia mienia"
  },
  [RisksId.ocStandard]: {
    displayName: "OC w życiu prywatnym",
    contextHelp: `<div>
                OC w życiu prywatnym (zakres terytorialny Europa) obejmuje m.in. szkody powstałe w związku z:
                <ul>
                    <li>- posiadaniem i użytkowaniem nieruchomości i ruchomości w miejscu ubezpieczenia,</li>
                    <li>- amatorskim uprawianiem sportów (jazda na rowerze, nartach po stoku),</li>
                    <li>- użytkowaniem wózków inwalidzkich,</li>
                    <li>- opieką nad niepełnoletnimi dziećmi i osobami niepełnosprawnymi, zaspakajaniem potrzeb noclegowych do 30dni pobyt np. w hotelu, posiadaniem niezabudowanych gruntów na terenie Polski,</li>
                    <li>- posiadaniem i użytkowaniem elektrycznych: hulajnóg, deskorolek, rowerów, skymaster, segway i segway shows oraz koni w celach rekreacyjnych, wyczynowym uprawianiem sportu oraz jazdy konnej.</li>
                </ul>
            </div>`
  },
  [RisksId.ocAdditionalProperty]: {
    displayName: "OC rozszerzam na dodatkową nieruchomość",
    contextHelp: `<div>
                Ochrona OC w życiu prywatnym może być rozszerzona o szkody powstałe w związku z posiadaniem lub użytkowaniem
                domu jednorodzinnego lub mieszkania w innej lokalizacji niż wskazanej w miejscu ubezpieczenia mienia.
                Proszę podać adres nieruchomości.
            </div>`
  },
  [RisksId.ocChildExtension]: {
    displayName: "Szkody wyrządzone przez dzieci do 13 r.ż.",
    contextHelp: `<div>
               TUZ Ubezpieczenia ponosi odpowiedzialność za szkody osobowe i rzeczowe spowodowane przez dzieci,
                które w chwili wyrządzenia szkody nie ukończyły 13 lat, bez określania winy w nadzorze przedstawiciela ustawowego.
            </div>`
  },
  [RisksId.ocRenterExtension]: {
    displayName: "OC Najemcy",
    contextHelp: `<div>
                Ochrona OC może być rozszerzona o odpowiedzialność cywilną najemcy o szkody rzeczowe w nieruchomości w
                miejscu ubezpieczenia, stałych elementach i ruchomościach domowych stanowiących  własność wynajmującego,
                a także w ruchomościach innych najemców (osób trzecich) korzystających z nieruchomości.
            </div>`
  },
  [RisksId.legalProtection]: {
    displayName: "Ochrona prawna",
    icon: <LegalProtection className="icon-svg-color" />,
    contextHelp: `<div>
                Przedmiotem ubezpieczenia jest pokrycie niezbędnych i prawnie uzasadnionych kosztów pomocy prawnej
                Ubezpieczonemu, powstałych w następstwie wypadku ubezpieczeniowego, pozostającego w związku z ich życiem
                prywatnym (nie związanym z prowadzeniem działalności gospodarczej)
        </div>`
  },
  [RisksId.nnwWithoutProperty]: {
    displayName: "Ubezpieczenie NNW bez ubezpieczenia mienia"
  },
  [RisksId.nnwType]: {
    displayName: "NNW",
    contextHelp: `<div>
                Przedmiotem Ubezpieczenia Następstw Nieszczęśliwych Wypadków są następstwa nieszczęśliwych wypadków na
                terenie Polski i poza granicami kraju, takie jak:
                <ul>
                    <li>trwały uszczerbek na zdrowiu w wyniku nieszczęśliwego wypadku,</li>
                    <li>śmierci w wyniku nieszczęśliwego wypadku,</li>
                    <li>koszty (nabycia, wypożyczenia lub naprawy) przedmiotów ortopedycznych i środków pomocniczych,</li>
                    <li>dzienne świadczenia szpitalne.</li>
                </ul>
                Ochrone można rozszerzyć o następstwa zawału serca i mózgu dla osób do 60 roku życia.
            </div>`
  },
  [RisksId.nnwSenior]: {
    displayName: "w tym pow. 60 roku życia"
  },
  [RisksId.nnwHeartAttackAndStroke]: {
    displayName: "Rozszerzenie zawał i udar"
  },
  [RisksId.gravestone]: {
    displayName: "Ubezpieczenie nagrobków",
    icon: <Gravestone className="icon-svg-color" />,
    contextHelp: `<div>
                    Zakres ubezpieczenia nagrobków cmentarnych obejmuje następujące ryzyka: silny wiatr, deszcz nawalny, dym i sadza, powódź, pożar,
                    uderzenie pioruna, upadek przedmiotu, upadek statku powietrznego, trzęsienie ziemi oraz kradzież i dewastacja.
                </div>`
  },
  [RisksId.safeSenior]: {
    displayName: "Bezpieczny senior",
    icon: <SafeSenior className="icon-svg-color" />,
    contextHelp: `<div>
                Martwisz się o swoich rodziców, dziadków czy starszą osobę, którą masz pod opieką. Nie masz na tyle czasu,
                aby w razie choroby lub nieszczęśliwego wypadku poświęcić czas na dostawę leków czy pomoc w domu, chcesz
                aby w razie nieprzewidzianej sytuacji starsza osoba Ci bliska mogła skorzystać z opieki lekarza czy pielęgniarki,
                aby był ktoś kto może sprawdzić jak się czuje, gdy nie odbiera telefonu, bo być może zdarzył się nieszczęśliwy wypadek.
                Ubezpieczenie Bezpieczny Senior zorganizuje wizytę lekarza, czy pielęgniarki, zapewni pomoc domową, dostawę leków czy
                zakupów, a nawet transport do placówki. Dodatkowo w przypadku wyłudzenia środków pieniężnych na wnuczka czy policję
                wypłacimy do 3 tys zł.
            </div>`
  },
  [RisksId.secondOpinion]: {
    displayName: <>Jedna osoba* ubezpieczona w ramach ubezpieczenia mienia jest objęta dodatkową ochroną <b>Ubezpieczenia
      Second Opinion – Druga Opinia Medyczna</b></>,
    icon: <SecondOpinion className="icon-svg-color" />,
    contextHelp: `<div>
                <p>
                    Druga Opinia Medyczna jest świadczeniem, którego celem jest weryfikacja trafności diagnozy oraz planu
                    leczenia. Sporządzana jest w oparciu o dokumentację medyczną bez udziału chorego.
                </p>
                <div>
                    Celem drugiej opinii medycznej jest zapewnienie:
                    <ul>
                        <li>
                            <b>Właściwej kompetencji</b> – zapewnienie jak najlepszego eksperta z dziedziny medycznej,
                            której dotyczy choroba
                        </li>
                        <li>
                            <b>Komfortu pacjenta podczas procesu diagnostyczno-terapeutycznego</b> – choremu dedykowany
                            jest polski lekarz z Europejskiego Instytutu Ekspertyz Medycznych, który będzie do jego
                            dyspozycji przez cały proces uzyskania drugiej opinii medycznej
                        </li>
                        <li>
                            <b>Skuteczności, a więc dostarczenie wiarygodnej i możliwie pełnej informacji odnośnie
                            trafności diagnozy i planu leczenia, rekomendacji alternatywnych metod leczenia</b> ze
                            wskazaniem na statystyki z nimi związane.
                        </li>
                    </ul>
                </div>
            </div>`
  },
  [RisksId.secondOpinionExtension]: {
    displayName: "Rozszerzam ochronę na osoby bliskie* pozostające z ubezpieczonym na rzecz, którego zawarta została umowa we wspólnym gospodarstwie domowym",
    contextHelp: `<div>
                <p>
                    Rozszerzenie ochrony Ubezpieczenia Second Opinion – Druga Opinia Medyczna na osoby bliskie pozostające
                    z ubezpieczonym na rzecz, którego zawarta została umowa we wspólnym gospodarstwie domowym.
                </p>
                <p>
                    Osoby bliskie: / dziecko / małżonek / partner życiowy / brat / siostra / matka / macocha / ojciec /
                    ojczym / pasierb / pasierbica / wnuk / dziadek / babcia / teściowa / teść / synowa / zięć /
                    przysposobiony / przysposabiający.
               </p>
            </div>`,
    displayNotification: " Czy na pewno chcesz zrezygnować z Drugiej Opinii Medycznej, z której będzie mogła skorzystać osoba Ci bliska (zgodnie z OWU) w przypadku ciężkiej choroby (zgodnie z OWU)?"

  }
};
